import { useRef } from 'react';
import type { Editor } from '@tiptap/react';
import type { Node as PMNode } from '@tiptap/pm/model';
import { Field, Input } from '@fluentui/react-components';

import useFocusNewFieldLabel from '../../../hooks/useFocusNewFieldLabel.hook';
import useTypedSetting from '../../../hooks/useTypedSetting.hook';
import type { TFieldSettings } from './fieldSettings';

type TProps = {
  editor: Editor;
  field: PMNode;
  onSettingsChange: (patch: Partial<TFieldSettings>) => void;
  isLabelMarked?: boolean;
};

/** A field's Label and Description, each above its input: in the field's card and in its popup. */
const FieldTextSettings = ({ editor, field, isLabelMarked, onSettingsChange }: TProps) => {
  const { label, description } = field.attrs as TFieldSettings;
  const [typedLabel, setTypedLabel] = useTypedSetting(field.attrs.fieldId, label);
  const [typedDescription, setTypedDescription] = useTypedSetting(field.attrs.fieldId, description);
  const refLabel = useRef<HTMLInputElement>(null);
  const refDescription = useRef<HTMLInputElement>(null);

  useFocusNewFieldLabel(editor, field.attrs.fieldId, refLabel);

  return (
    <>
      <Field label="Label" size="small" validationState={isLabelMarked ? 'error' : 'none'}>
        <Input
          size="small"
          value={typedLabel}
          input={{ ref: refLabel }}
          onChange={(_, data) => {
            setTypedLabel(data.value);
            onSettingsChange({ label: data.value });
          }}
          onKeyDown={(event) => {
            if (event.key !== 'Enter') return;
            event.preventDefault();
            refDescription.current?.focus();
          }}
        />
      </Field>
      <Field label="Description" size="small">
        <Input
          size="small"
          value={typedDescription}
          input={{ ref: refDescription }}
          onChange={(_, data) => {
            setTypedDescription(data.value);
            onSettingsChange({ description: data.value });
          }}
        />
      </Field>
    </>
  );
};

export default FieldTextSettings;
